import { createAsyncThunk } from "@reduxjs/toolkit"
import { ProcessStageStatus, ProjectProcess, ProjectProcessHeader } from "../projectProcess/ProjectProcessApiClient"
import { AppId, AppThunkAPIType } from "../../../app/appTypes"
import { getWithAuth, postWithAuth } from "../../../http"
import { DocumentLanguages, DocumentTypes } from "../../documents/documentTypes";
import { DocumentKind } from "../../documents/documentsApi";

export type ValidationScenario = "NEW" | "REQUAL_PERIODIC" | "REQUAL_CHANGE";
export type ValidationObjectType = "DEVICE" | "COMPUTERIZED_SYSTEM" | "ROOM";
export type UrsStageMode = "EXISTING" | "LATER" | "SKIP" | "";
export type ObjectValidationStatus = "NOT_VALIDATED" | "NOT_APPLICABLE" | "IN_PROGRESS" | "VALIDATED" | "REVALIDATION_REQUIRED"

export type ValidationObject =
    | {
        mode: "NEW";
        name: string | null;
        serialNo: string | null;
        structureId: AppId | null;
    }
    | {
        mode: "EXISTING";
        id: string;
    };

export interface ValidationProcessInfo {
    number: string;
    title: string;
    projectId?: number | null;
    projectManager: string;
    projectTeam: ProcessTeamMember[];
}

export interface UrsInfo {
    mode: UrsStageMode;
    comment: string;
    id: string | null;
}

export interface ProcessTeamMember {
    userName: string;
    role: string;
    isExternal: boolean
};


export interface CreateValidationProcessPayload {
    objectType: ValidationObjectType | null;
    scenario: ValidationScenario | null;
    object: ValidationObject;
    process: ValidationProcessInfo;
    urs: UrsInfo;
}

export interface ProcessStageOverview {
    id: string
    stage: string;
    status: ProcessStageStatus;
    order: number;
    processId: number;
    lastActionByUserId: string
    lastActionType: string
    lastActionAtUtc: Date
    lastActionByUserFullName: string
    comment: string
    documents: ProcessDocumentOverview[];
}


export interface ProcessDocumentOverview {
    id: number
    title: string;
    no: string;
    language: string;
    type: DocumentTypes;
    documentKind: DocumentKind;
    status: string;
    guid: string;
    createDate: Date;
    version: number;
    responsibleUserName: string;
    responsibleFullName: string;
}


export const loadValidationProcesses = createAsyncThunk<ProjectProcessHeader[], { filter: string }, AppThunkAPIType>(
    "validationProcesses/loadAll", async ({ filter }, { dispatch, rejectWithValue }) => {
        const url = filter ? `api/validation-processes?${filter}` : "api/validation-processes"
        const result = await dispatch(getWithAuth({ url }))
        const { payload } = result

        if (getWithAuth.fulfilled.match(result)) {
            return payload as ProjectProcessHeader[]
        } else {
            return rejectWithValue(payload ?? { kind: 'unknown' })
        }
    })

export const loadValidationProcessForSystem = createAsyncThunk<
    ProjectProcessHeader[],
    number,
    AppThunkAPIType
>(
    "validationProcesses/loadForSystem",
    async (systemId, { dispatch, rejectWithValue }) => {
        const result = await dispatch(
            getWithAuth({ url: `api/validation-processes/systems/${systemId}` })
        );
        const { payload } = result;
        if (getWithAuth.fulfilled.match(result)) {
            return payload as ProjectProcessHeader[];
        } else {
            return rejectWithValue(payload ?? { kind: "unknown" });
        }
    }
);

export const loadValidationProcessForDevice = createAsyncThunk<
    ProjectProcessHeader[],
    AppId,
    AppThunkAPIType
>(
    "validationProcesses/loadForDevice",
    async (deviceId, { dispatch, rejectWithValue }) => {
        const result = await dispatch(
            getWithAuth({ url: `api/validation-processes/devices/${deviceId}` })
        );
        const { payload } = result;
        if (getWithAuth.fulfilled.match(result)) {
            return payload as ProjectProcessHeader[];
        } else {
            return rejectWithValue(payload ?? { kind: "unknown" });
        }
    }
);
export const loadValidationProcessForRoom = createAsyncThunk<
    ProjectProcessHeader[],
    number,
    AppThunkAPIType
>(
    "validationProcesses/loadForRoom",
    async (roomId, { dispatch, rejectWithValue }) => {
        const result = await dispatch(
            getWithAuth({ url: `api/validation-processes/rooms/${roomId}` })
        );
        const { payload } = result;
        if (getWithAuth.fulfilled.match(result)) {
            return payload as ProjectProcessHeader[];
        } else {
            return rejectWithValue(payload ?? { kind: "unknown" });
        }
    }
);

export const loadValidationProcessStageOverview = createAsyncThunk<ProcessStageOverview[], string, AppThunkAPIType>(
    "validationProcesses/loadValidationProcessStageOverview", async (id, { dispatch, rejectWithValue }) => {
        const result = await dispatch(getWithAuth({
            url: `api/project-process/${id}/stage-overview`,
        }))
        const { payload } = result
        if (getWithAuth.fulfilled.match(result)) {
            return payload as ProcessStageOverview[]
        } else {
            return rejectWithValue(payload ?? { kind: 'unknown' })
        }
    })



export const createValidationProcess = createAsyncThunk<ProjectProcess, CreateValidationProcessPayload, AppThunkAPIType>(
    "validation-processes/createValidationProcess",
    async (createProcessPayload: CreateValidationProcessPayload, { rejectWithValue, dispatch }) => {
        const result = await dispatch(postWithAuth({
            url: "api/validation-processes",
            payload: createProcessPayload,
        }))
        const { payload } = result

        if (postWithAuth.fulfilled.match(result)) {
            return payload as ProjectProcess
        } else {
            return rejectWithValue(payload ?? { kind: 'unknown' })
        }
    }
)

export type CreationMode =
    | "EMPTY"
    | "TEMPLATE"

export interface RiskAnalysisCreateSettings {
    isIshikawa: boolean;
    loadRequirementsFromUrs: boolean;
    LoadFromACF: boolean;
}

export interface DocumentToCreate {
    type: DocumentTypes;
    number: string;
    title: string;
    language: DocumentLanguages;
    responsible?: string | null;
    creationMode: CreationMode;
    templateId: number;
    raSettings?: RiskAnalysisCreateSettings;
}

export interface DocumentToLink {
    type: DocumentTypes;
    documentId: number;
}


export interface PrepareValidationStagePayload {
    stageCode: string;
    processId: AppId;
    documents: DocumentToCreate[];
    toLink: DocumentToLink[],
    removeExistingLinks?: boolean
}

export const prepareValidationStage = createAsyncThunk<ProcessStageOverview, PrepareValidationStagePayload, AppThunkAPIType>(
    "validation-processes/createValidationProcess",
    async (createPayload: PrepareValidationStagePayload, { rejectWithValue, dispatch }) => {
        const result = await dispatch(postWithAuth({
            url: `api/validation-processes/${createPayload.processId}/stages/prepare-stage`,
            payload: createPayload,
        }))
        const { payload } = result

        if (postWithAuth.fulfilled.match(result)) {
            return payload as ProcessStageOverview
        } else {
            return rejectWithValue(payload ?? { kind: 'unknown' })
        }
    }
)

export interface AddStageDocumentPayload {
    processId: AppId;
    stageId: string;
    document: DocumentToCreate;
}

export const addStageDocument = createAsyncThunk<ProcessStageOverview, AddStageDocumentPayload, AppThunkAPIType>(
    "validation-processes/addStageDocument",
    async (createPayload: AddStageDocumentPayload, { rejectWithValue, dispatch }) => {
        const result = await dispatch(
            postWithAuth({
                url: `api/validation-processes/${createPayload.processId}/stages/${createPayload.stageId}/documents`,
                payload: createPayload.document
            })
        );
        const { payload: data } = result;

        if (postWithAuth.fulfilled.match(result)) {
            return data as ProcessStageOverview;
        } else {
            return rejectWithValue(data ?? { kind: "unknown" });
        }
    }
);


export interface ChangeValidatedObjectPayload {
    processId: AppId;
    objectType: string;
    objectId: string;
}

export const changeValidatedObject = createAsyncThunk<ProcessStageOverview, ChangeValidatedObjectPayload, AppThunkAPIType>(
    "validation-processes/createValidationProcess",
    async (changeValidatedObject: ChangeValidatedObjectPayload, { rejectWithValue, dispatch }) => {
        const result = await dispatch(postWithAuth({
            url: `api/validation-processes/${changeValidatedObject.processId}/change-validated-object`,
            payload: changeValidatedObject,
        }))
        const { payload } = result

        if (postWithAuth.fulfilled.match(result)) {
            return payload as ProcessStageOverview
        } else {
            return rejectWithValue(payload ?? { kind: 'unknown' })
        }
    }
)


export interface SkipUrsStagePayload {
    processId: AppId;
    comment: string;
}
export const skipUrsStage = createAsyncThunk<ProcessStageOverview, SkipUrsStagePayload, AppThunkAPIType>(
    "validation-processes/skipUrsStage",
    async (createPayload: SkipUrsStagePayload, { rejectWithValue, dispatch }) => {
        const result = await dispatch(postWithAuth({
            url: `api/validation-processes/${createPayload.processId}/stages/urs/skip`,
            payload: createPayload,
        }))
        const { payload } = result

        if (postWithAuth.fulfilled.match(result)) {
            return payload as ProcessStageOverview
        } else {
            return rejectWithValue(payload ?? { kind: 'unknown' })
        }
    }
)

export type SkipStagePayload = {
    processId: string;
    stageId: string;
    comment: string;
};

export const skipStage = createAsyncThunk<ProcessStageOverview, SkipStagePayload, AppThunkAPIType>(
    "validation-processes/skipStage",
    async (payload, { dispatch, rejectWithValue }) => {
        const result = await dispatch(
            postWithAuth({
                url: `api/validation-processes/${payload.processId}/stages/${payload.stageId}/skip`,
                payload: {
                    comment: payload.comment ?? "",
                },
            })
        );

        const { payload: data } = result;

        if (postWithAuth.fulfilled.match(result)) {
            return data as ProcessStageOverview;
        } else {
            return rejectWithValue(data ?? { kind: "unknown" });
        }
    }
);


export type UnSkipStagePayload = {
    processId: string;
    stageId: string;
    comment: string;
};

export const unskipStage = createAsyncThunk<ProcessStageOverview, UnSkipStagePayload, AppThunkAPIType>(
    "validation-processes/unskipStage",
    async (payload, { dispatch, rejectWithValue }) => {
        const result = await dispatch(
            postWithAuth({
                url: `api/validation-processes/${payload.processId}/stages/${payload.stageId}/unskip`,
                payload: {
                    comment: payload.comment ?? "",
                },
            })
        );

        const { payload: data } = result;

        if (postWithAuth.fulfilled.match(result)) {
            return data as ProcessStageOverview;
        } else {
            return rejectWithValue(data ?? { kind: "unknown" });
        }
    }
);

export const prepareUrsStage = createAsyncThunk<ProcessStageOverview, PrepareValidationStagePayload, AppThunkAPIType>(
    "validation-processes/createValidationProcess",
    async (createPayload: PrepareValidationStagePayload, { rejectWithValue, dispatch }) => {
        const result = await dispatch(postWithAuth({
            url: `api/validation-processes/${createPayload.processId}/stages/prepare-stage/urs`,
            payload: createPayload,
        }))
        const { payload } = result

        if (postWithAuth.fulfilled.match(result)) {
            return payload as ProcessStageOverview
        } else {
            return rejectWithValue(payload ?? { kind: 'unknown' })
        }
    }
)


export interface GenerateReportForProtocolPayload {
    stageId: string;
    processId: AppId;
    documentId: number;
    documentNumber: string;
    documentTitle: string;
    copyTeamFromProtocol: boolean;
    responsiblePersonName: string;
}

export const generateReportForProtocol = createAsyncThunk<ProcessStageOverview, GenerateReportForProtocolPayload, AppThunkAPIType>(
    "validation-processes/generateReportForProtocol",
    async (createPayload: GenerateReportForProtocolPayload, { rejectWithValue, dispatch }) => {
        const result = await dispatch(postWithAuth({
            url: `api/validation-processes/${createPayload.processId}/stages/${createPayload.stageId}/generate-report/${createPayload.documentId}`,
            payload: createPayload,
        }))
        const { payload } = result

        if (postWithAuth.fulfilled.match(result)) {
            return payload as ProcessStageOverview
        } else {
            return rejectWithValue(payload ?? { kind: 'unknown' })
        }
    }
)

